<template>
	<div>
		<ModalHeader
			:title="getString('connect_terminal')"
			:button-text="readerType !== null ? getString('connect') : null"
			:button-loading="loading"
			@click-button="clickActivate" />
		<div class="reader-type-chooser" v-if="readerType === null">
			<AppButton outline :text="isAndroid ? getString('tap_on_phone') : getString('tap_phone_ios')" @click="readerType = 'tap_on_phone'" />
			<AppButton outline :text="getString('bluetooth')" @click="readerType = 'bluetooth'" />
			<AppButton v-if="isAndroid" outline :text="getString('handoff')" @click="readerType = 'handoff'" />
		</div>
		<div class="tap-phone-container" v-else-if="readerType === 'tap_on_phone'">
			<div class="explain-tap-phone-android" v-if="isAndroid">
				<div class="img-container mt-5">
					<img src="https://old.img.qromo.io/img/phone-min.png" alt="banner tap on phone">
				</div>
				<p class="mt-15">{{ getString('explain_tap_on_the_phone') }}</p>
				<AppButton class="mt-15 ml-auto" outline :text="getString('cancel')" @click="readerType = null" />
			</div>
			<div class="explain-tap-phone-ios" v-else>
				<AppPagination class="pagination" change-height ref="pagination-ios" :pages="listSections">
					<template v-slot:step1="{step1}">
						<div class="merchant-education-step">
							<div class="merchant-education-title">{{ getString('merchant_education_title_1') }}</div>
							<div class="merchant-education-image">
								<img src="https://img.qromo.io/img/merchant-edu-1.png" alt="banner tap on phone">
							</div>
							<div class="merchant-education-explain" v-html="getString('merchant_education_description_1')"></div>
						</div>
					</template>
					<template v-slot:step2="{step2}">
						<div class="merchant-education-step">
							<div class="merchant-education-title">{{ getString('merchant_education_title_2') }}</div>
							<div class="merchant-education-image">
								<img src="https://img.qromo.io/img/merchant-edu-2.png" alt="banner tap on phone">
							</div>
							<div class="merchant-education-explain" v-html="getString('merchant_education_description_2')"></div>
						</div>
					</template>
					<template v-slot:step3="{step3}">
						<div class="merchant-education-step">
							<div class="merchant-education-title">{{ getString('merchant_education_title_3') }}</div>
							<div class="merchant-education-image">
								<img src="https://img.qromo.io/img/merchant-edu-3.png" alt="banner tap on phone">
							</div>
							<div class="merchant-education-explain" v-html="getString('merchant_education_description_3')"></div>
						</div>
					</template>
					<template v-slot:step4="{step4}">
						<div class="merchant-education-step">
							<div class="merchant-education-title">{{ getString('merchant_education_title_4') }}</div>
							<div class="merchant-education-image">
								<img src="https://img.qromo.io/img/merchant-edu-4.png" alt="banner tap on phone">
							</div>
							<div class="merchant-education-explain" v-html="getString('merchant_education_description_4')"></div>
						</div>
					</template>
				</AppPagination>
			</div>
		</div>
		<div class="bluetooth-container" v-else-if="readerType === 'bluetooth'">
			<div class="img-container mt-5">
				<img src="https://img.qromo.io/img/banner-bluetooth.png" alt="banner bluetooth">
			</div>
			<div class="standard-connection" v-if="sunmiStore.bluetoothUpdateProgress === null">
				<p class="mt-15">{{ getString('explain_bluetooth') }}</p>
				<AppButton class="mt-15 ml-auto" outline :text="getString('cancel')" @click="readerType = null" />
			</div>
			<div class="update-required" v-else>
				<p class="mt-15 mb-15">{{ getString('explain_bluetooth_update') }}</p>
				<AppProgressBar :progress="updateProgress" />
			</div>
		</div>
		<div class="handoff-container" v-else-if="readerType === 'handoff'">
			<div class="img-container mt-5">
				<img src="https://img.qromo.io/img/banner-s700.jpg" alt="banner s700">
			</div>
			<p class="mt-15">{{ getString('explain_handoff') }}</p>
			<AppButton class="mt-15 ml-auto" outline :text="getString('cancel')" @click="readerType = null" />
		</div>
		<AppNotification ref="fail-unauthorized" :title="getString('error')" :body-text="getString('unauthorized')" :type="-2" />
		<AppNotification ref="invalid-address" :title="getString('error')" :body-text="getString('invalid_address')" :type="-2" />
		<AppNotification ref="fail-nfc-connection" :title="getString('error')" :body-text="getString('fail_nfc_connection')" :type="-2" />
		<AppNotification ref="generic-error" :title="getString('error')" :body-text="getString('generic_error')" :type="-2" />
	</div>
</template>

<script>
import ModalHeader from "@/console-new/components/shared/ModalHeader.vue";
import {useLanguageStore} from "@/shared/stores";
import AppNotification from "@/shared/components/AppNotification.vue";
import {useSunmiStore, useUserStore} from "@/console-sun/stores";
import AppButton from "@/shared/components/AppButton.vue";
import AppProgressBar from "@/shared/components/AppProgressBar.vue";
import {getNative, isAndroid, isNative} from "@/shared/helpers/webapp";
import AppPagination from "@/shared/components/AppPagination.vue";
import CompositeEditorOptions from "@/console-new/components/config/menu/CompositeEditorOptions.vue";
import AppTabsSwitch from "@/shared/components/AppTabsSwitch.vue";
import sunLangObject from "@/console-sun/assets/json/langs.json";

export default {
	setup() {
		const languageStore = useLanguageStore();
		const userStore = useUserStore();
		const sunmiStore = useSunmiStore();
		return {languageStore, getString: languageStore.getString, userStore, sunmiStore};
	},
	emits: ["done"],
	name: "LocalTerminalSheet",
	components: {AppTabsSwitch, CompositeEditorOptions, AppPagination, AppProgressBar, ModalHeader, AppNotification, AppButton},
	data() {
		return {
			loading: false,
			readerType: null,
			listSections: [
				{id: 'step1', slotName: 'step1'},
				{id: 'step2', slotName: 'step2'},
				{id: 'step3', slotName: 'step3'},
				{id: 'step4', slotName: 'step4'},
			],
		}
	},
	computed: {
		updateProgress() {
			if(this.sunmiStore.bluetoothUpdateProgress !== null) {
				return Math.min(Math.max(this.sunmiStore.bluetoothUpdateProgress, 0), 100);
			} else {
				return 0;
			}
		},
		isAndroid() {
			return isAndroid();
		}
	},
	methods: {
		clickActivate() {
			if(this.readerType !== null) {
				if(isNative()) {
					this.loading = true;
					this.userStore.getBusinessLocation((locationId, details) => {
						if(locationId) {
							let refreshToken = localStorage.getItem("refresh_token");
							if (refreshToken) {
								if(isAndroid()) {
									getNative().activateLocalTerminal(refreshToken, locationId, this.userStore.currBusinessId, this.readerType);
								} else {
									getNative().activateLocalTerminalV2(refreshToken, this.userStore.currBusiness.stripe_account, locationId, this.userStore.currBusinessId, this.readerType);
								}
							} else {
								this.$refs["generic-error"].openNotification();
							}
						} else if(details === "auth_denied") {
							this.$refs["fail-unauthorized"].openNotification();
						} else if(details === "missing_valid_address") {
							this.$refs["invalid-address"].openNotification();
						} else {
							this.$refs["generic-error"].openNotification();
						}
					})
				}
			}
		},
	},
	mounted() {
		this.sunmiStore.bluetoothUpdateProgress = null;
	},
	created() {
		this.languageStore.addStrings(sunLangObject, "sun");
	},
	watch: {
		"sunmiStore.failedTapPhoneConnection": {
			handler(newValue, oldValue) {
				if(oldValue !== newValue && newValue) {
					this.sunmiStore.failedTapPhoneConnection = false;
					this.$refs["fail-nfc-connection"].openNotification();
					this.loading = false;
				}
			}
		},
	}
}
</script>

<style scoped lang="scss">
.reader-type-chooser {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	margin-top: 20px;

	.base-button {
		width: 100%;
		max-width: 300px;
		margin-bottom: 10px;
	}

}
.img-container {
	display: flex;
	justify-content: center;
	align-items: center;
	width: 100%;

	img {
		width: 100%;
		height: 100%;
		object-fit: cover;
		border-radius: 13px;
		max-width: 260px;
	}
}

.tap-phone-container {

	.merchant-education-step {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		margin-bottom: 20px;

		.merchant-education-title {
			font-size: 18px;
			font-weight: bold;
			margin-bottom: 10px;
		}

		.merchant-education-image {
			height: 240px;
			width: 350px;
			margin-bottom: 10px;

			img {
				object-fit: contain;
				width: 100%;
				height: 100%;
				border-radius: 13px;
			}
		}

		.merchant-education-explain {
			text-align: left;
			font-size: 14px;
			margin-top: 10px;
			color: var(--color-text-secondary);
		}
	}
}
.ml-auto {
	margin-left: auto;
}
</style>